import {
  combineReducers,
  configureStore,
  type Action,
  type Store,
} from '@reduxjs/toolkit';
import {
  markPerformance,
  Marks,
} from '@/client/utilities/performance/performanceMonitor';
import { env } from './env';
import { filtersConfig } from './filtersConfig';
import { items } from './items';
import { search } from './search';
import { system } from './system';

const rootReducer = combineReducers({
  system,
  search,
  items,
  env,
  filtersConfig,
});

export type RootState = ReturnType<typeof rootReducer>;

export type AppStore = ReturnType<typeof getStore>;

export type AppDispatch = AppStore['dispatch'];

let store: Store<RootState> | undefined;

const DEFAULT_MW_OPTIONS = {
  serializableCheck: false, // Temporarily disable serializability check
};

const createBaseStore = (preloadedState?: RootState) =>
  configureStore({
    devTools: process.env.NODE_ENV !== 'production',
    preloadedState,
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware(DEFAULT_MW_OPTIONS),
  });

export const getStore = (
  preloadedState: RootState | undefined,
  isServer = false
): Store<RootState> => {
  // For server rendering, always create a new store
  if (isServer) {
    return createBaseStore(preloadedState);
  }

  // If the store has already been initialized, return it
  if (store) {
    return store;
  }

  // Disable React DevTools in production
  if (
    process.env.NODE_ENV === 'production' &&
    window.__REACT_DEVTOOLS_GLOBAL_HOOK__
  ) {
    window.__REACT_DEVTOOLS_GLOBAL_HOOK__.inject = () => undefined;
  }

  markPerformance(Marks.CREATE_STORE);

  store = createBaseStore(preloadedState);

  // In development environment, initialize the store with redux-logger
  if (process.env.NODE_ENV !== 'production') {
    (async () => {
      try {
        const { createLogger } = await import('redux-logger');

        const logger = createLogger({
          collapsed: true,
          predicate: (_, action: Action) => !action.type.startsWith('@@'),
        });

        // Override the store with one that includes the logger
        store = configureStore({
          preloadedState,
          reducer: rootReducer,
          devTools: true,
          middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware(DEFAULT_MW_OPTIONS).concat(logger),
        });
      } catch (exception) {
        console.warn('Failed to load Redux logger:', exception);
      }
    })();
  }

  return store;
};
